import { getUITranslation } from "../utils/get_ui_translation";
import { fullname, normalizeString } from "../utils/format";
import { naturalCompare } from "../utils/string";

/* global Bloodhound */

let bloodhounds = {};
let traineesBloodhounds = {};

//----------------------------------------------------------------------------------------
// createOnLoad
//----------------------------------------------------------------------------------------
export function createOnLoad($root) {
  if (!$root) {
    $root = $(document);
  }
  $root.find("[data-autocomplete]").each(function (i, e) {
    let $e = $(e);
    let type = $e.attr("data-autocomplete");
    if (type == "company-name") {
      initCompanyAutocomplete($e);
    }
    if (type == "funding-agency-name") {
      initFundingAgencyAutocomplete($e);
    }
    if (type == "instructor-name") {
      initInstructorAutocomplete($e);
    } else if (type == "trainee-name") {
      initTraineeNameAutocomplete($e);
    } else if (type == "trainee-firstname") {
      initTraineeFirstNameAutocomplete($e);
    } else if (type == "trainee-lastname") {
      initTraineeLastNameAutocomplete($e);
    } else if (type == "trainee-email") {
      initTraineeEmailAutocomplete($e);
    }
  });
}

//----------------------------------------------------------------------------------------
// initFundingAgencyAutocomplete
//----------------------------------------------------------------------------------------
let initFundingAgencyAutocomplete = initAutocomplete(
  p`/ajax/funding_agencies/autocomplete`,
  "funding_agencies",
  "funding-agency",
  getUITranslation("Créer un nouveau financeur externe"),
  getUITranslation("Aucun financeur externe trouvé")
);

//----------------------------------------------------------------------------------------
// initCompanyAutocomplete
//----------------------------------------------------------------------------------------
let initCompanyAutocomplete = initAutocomplete(
  p`/ajax/companies/autocomplete`,
  "companies",
  "company",
  getUITranslation("Créer une nouvelle entreprise"),
  getUITranslation("Aucune entreprise trouvée")
);

//----------------------------------------------------------------------------------------
// initInstructorAutocomplete
//----------------------------------------------------------------------------------------
const initInstructorAutocomplete = initAutocomplete(
  p`/ajax/instructors/autocomplete`,
  "instructors",
  "instructor",
  getUITranslation("Créer un nouvel intervenant"),
  getUITranslation("Aucun intervenant trouvé"),
  function (t) {
    return fullname(t.firstname, t.lastname);
  }
);

//----------------------------------------------------------------------------------------
// initAutocomplete
//----------------------------------------------------------------------------------------
export function initAutocomplete(url, field, fieldCss, createMessage, notFoundMessage, nameExtractor) {
  nameExtractor =
    nameExtractor ||
    function (t) {
      return t.name;
    };
  return function ($e) {
    // Create the Bloodhound
    if (bloodhounds[field] === undefined) {
      bloodhounds[field] = new Bloodhound({
        datumTokenizer: function (datum) {
          return Bloodhound.tokenizers.whitespace(nameExtractor(datum));
        },
        queryTokenizer: Bloodhound.tokenizers.whitespace,
        identify: function (datum) {
          return datum.id;
        },
        prefetch: {
          url,
          transform: function (response) {
            return field.includes("refresh") ? response[fieldCss] : response[field];
          },
          cache: false,
        },
      });
    }

    let updateInput = function (e, datum) {
      let value = e.target.value;

      let item =
        datum ||
        _.find(bloodhounds[field].all(), function (c) {
          return nameExtractor(c).toLowerCase().trim() === value.toLowerCase().trim();
        });

      if (value.trim() == "") {
        $e.typeahead("val", value);
      }
      if (item) {
        $e.typeahead("val", value);
        $e.typeahead("close");
      }
      $e.triggerNative("autocomplete", item);
      notifyForViewModel($e);
    };

    let new_item_url = urlForField(field, { select_mode: "true", back_path: window.location.pathname + window.location.search });

    const canCreateNewEntity = $e.attr("data-can-create-new-entity");
    const createNewEntityLink = canCreateNewEntity === "true" ? renderAnchorButton(createMessage, new_item_url) : "";
    $e.typeahead(
      {
        hint: false,
        highlight: true,
        minLength: 0,
      },
      {
        name: field,
        source: (query, syncResults) => {
          const suggestions = bloodhounds[field].all().sort((a, b) => naturalCompare(nameExtractor(a), nameExtractor(b)));
          const datums = normalizeString(query).split(" ");

          const matching =
            query === ""
              ? suggestions
              : suggestions.filter((row) => {
                  const name = normalizeString(nameExtractor(row));
                  return datums.every((d) => name.includes(d));
                });

          syncResults(matching);
        },
        display: nameExtractor,
        limit: 10,
        templates: {
          footer: createNewEntityLink,
          notFound: `
            <div class="label label--md has-p-03">${notFoundMessage}</div>
            ${createNewEntityLink}
          `,
          suggestion: function (query) {
            return createSuggestionHTML(nameExtractor(query));
          },
        },
      }
    )
      .on("keyup", _.debounce(updateInput, 200))
      .on("typeahead:select", updateInput);
  };
}

function urlForField(field, params) {
  switch (field) {
    case "funding_agencies":
      return p`/funding_agencies/new?${params}`;
    case "companies":
      return p`/companies/new?${params}`;
    case "instructors":
      return p`/instructors/new?${params}`;
  }
}

//----------------------------------------------------------------------------------------
// initTraineeNameAutocomplete
//----------------------------------------------------------------------------------------
function initTraineeNameAutocomplete($e) {
  let new_trainee_url = p`/trainees/new?select_mode=true&back_path=${window.location.pathname + window.location.search}`;
  createTraineeTypeahead(
    $e,
    "name",
    {
      display: function (datum) {
        return fullname(datum.firstname, datum.lastname);
      },
      suggestion: function (query) {
        return createSuggestionHTML(traineeText(query));
      },
      footer: renderAnchorButton(getUITranslation("Créer un nouvel apprenant"), new_trainee_url),
      notFound: `
        <div class="label label--md has-p-03">${getUITranslation("Aucune personne avec ce nom")}</div>
        ${renderAnchorButton(getUITranslation("Créer un nouvel apprenant"), new_trainee_url)}
      `,
    },
    onSelectedTrainee($e, "name", function (t) {
      return fullname(t.firstname, t.lastname);
    })
  );
}

function renderAnchorButton(text, href) {
  return `
    <a class="button button--secondary button--md button--align-start" href="${href}">
     <svg role="img" fill="currentColor"  class="icon icon--sm">
      <use href="/icons/sprite.svg#plus" />
    </svg>
    ${text}</a>`;
}

function createSuggestionHTML(primaryText, secondaryText) {
  const suggestionEl = document.createElement("div");
  suggestionEl.setAttribute("class", "suggest-autocomplete");

  const suggestionSpan = document.createElement("span");
  suggestionSpan.textContent = primaryText;

  if (secondaryText) {
    const suggestionInfo = document.createElement("i");
    // Add a leading space for separation
    suggestionInfo.textContent = " " + secondaryText;
    suggestionSpan.appendChild(suggestionInfo);
  }

  suggestionEl.appendChild(suggestionSpan);

  return suggestionEl.outerHTML;
}

//----------------------------------------------------------------------------------------
// initTraineeFirstNameAutocomplete
//----------------------------------------------------------------------------------------
function initTraineeFirstNameAutocomplete($e) {
  createTraineeTypeahead(
    $e,
    "firstname",
    {
      display: function (datum) {
        return datum.firstname;
      },
      suggestion: function (query) {
        // Use the helper function to generate suggestion HTML
        const primary = fullname(query.firstname, query.lastname);
        const secondary = query.email ? query.email : "";
        return createSuggestionHTML(primary, secondary);
      },
    },
    onSelectedTrainee($e, "firstname")
  );
}

//----------------------------------------------------------------------------------------
// initTraineeLastNameAutocomplete
//----------------------------------------------------------------------------------------
function initTraineeLastNameAutocomplete($e) {
  createTraineeTypeahead(
    $e,
    "lastname",
    {
      display: function (datum) {
        return datum.lastname;
      },
      suggestion: function (query) {
        // Use the helper function to generate suggestion HTML
        const primary = fullname(query.firstname, query.lastname);
        const secondary = query.email ? query.email : "";
        return createSuggestionHTML(primary, secondary);
      },
    },
    onSelectedTrainee($e, "lastname")
  );
}

//----------------------------------------------------------------------------------------
// initTraineeEmailAutocomplete
//----------------------------------------------------------------------------------------
function initTraineeEmailAutocomplete($e) {
  createTraineeTypeahead(
    $e,
    "email",
    {
      display: function (datum) {
        return datum.email;
      },
      suggestion: function (query) {
        // Use the helper function to generate suggestion HTML
        const primary = query.email;
        const secondary = fullname(query.firstname, query.lastname);
        return createSuggestionHTML(primary, secondary);
      },
    },
    onSelectedTrainee($e, "email")
  );
}

function onSelectedTrainee($e, field, accessor) {
  let $parent = $e.parents(".form-group");

  if (!accessor) {
    accessor = function (t) {
      return t[field];
    };
  }

  return function (e, trainee) {
    let value = e.target.value;

    if (trainee === undefined && $e.__traineeSelected && accessor($e.__traineeSelected).toLowerCase() === value.toLowerCase()) {
      trainee = $e.__traineeSelected;
    }

    if (trainee) {
      $e.typeahead("val", accessor(trainee));
      $e.__traineeSelected = trainee;
      $parent.removeClass("not-found");
      $e.removeClass("not-found");
      $e.typeahead("val", value);
      $e.typeahead("close");
    } else {
      $e.__traineeSelected = null;
      $parent.addClass("not-found");
      $e.addClass("not-found");
    }
    $e.triggerNative("autocomplete", trainee);
    notifyForViewModel($e);
  };
}

function createTraineeTypeahead($e, field, opts, onSelectTrainee) {
  if (traineesBloodhounds[field] === undefined) {
    traineesBloodhounds[field] = createTraineeBloodhound(field);
  }

  $e.typeahead(
    {
      hint: false,
      highlight: true,
      minLength: 1,
    },
    {
      name: "trainees",
      limit: 20,
      source: traineesBloodhounds[field],
      display: opts.display,
      templates: {
        suggestion: opts.suggestion,
        footer: opts.footer,
        notFound: opts.notFound,
      },
    }
  )
    .on("typeahead:select", onSelectTrainee)
    .on("keyup", _.debounce(onSelectTrainee, 200));
}

function createTraineeBloodhound(field) {
  return new Bloodhound({
    datumTokenizer: function (datum) {
      return Bloodhound.tokenizers.whitespace(datum[field]);
    },
    queryTokenizer: Bloodhound.tokenizers.whitespace,
    identify: function (datum) {
      return datum.id;
    },
    remote: {
      /* eslint-disable-next-line digiforma/verified-routes */
      url: p`/ajax/trainees/autocomplete/${field}/QUERY`,
      wildcard: "QUERY",
      transform: function (response) {
        return response.trainees.sort((a, b) => naturalCompare(traineeText(a), traineeText(b)));
      },
    },
  });
}

function notifyForViewModel($e) {
  $e[0].dispatchEvent(new Event("change"));
}

function traineeText(trainee) {
  return fullname(trainee.firstname, trainee.lastname) + (trainee.email ? " - " + trainee.email : "");
}
